/**
 * Console nền tảng → chi tiết tổ chức → "Thương hiệu & tên miền": super admin gán tên miền
 * (có thể xác nhận tay khi đã kiểm DNS bên ngoài), cấp subdomain, sửa nhanh thương hiệu và
 * xem mức dùng theo gói.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, RefreshCw, Star, Trash2 } from 'lucide-react'
import { platformApi, platformApiError } from '@/lib/platform-client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Switch } from '@/components/ui/misc'
import { ChonMau } from '@/components/shared/chon-mau'

interface TenMien { id: string; domain: string; kind: string; status: string; isPrimary: boolean; sslStatus: string; lastError: string | null; huongDan: { txt: { ten: string; giaTri: string } } | null }
interface DuLieuTenMien {
  domains: TenMien[]; nenTang: string | null
  hanMuc: { goi: { ten: string } | null; gioiHan: Record<string, number | null | boolean>; dangDung: Record<string, number> }
}
interface ThuongHieu { brandName: string | null; tagline: string | null; primaryColor: string | null; headerColor: string | null; hidePlatformBrand: boolean }

export interface GoiDichVu { key: string; ten: string; moTa: string; isActive?: boolean }

export function usePlatformPlans() {
  return useQuery<{ plans: GoiDichVu[] }>({ queryKey: ['platform', 'plans'], queryFn: async () => (await platformApi.get('/platform/plans')).data, staleTime: 10 * 60_000 })
}

const TEN_HM: Record<string, string> = { nguoiDung: 'Người dùng', nickZalo: 'Nick Zalo', webhook: 'Webhook', tenMienRieng: 'Tên miền riêng' }

export function OrgWhitelabelPlatform({ orgId }: { orgId: string }) {
  const qc = useQueryClient()
  const khoa = ['platform', 'org', orgId, 'domains']
  const { data } = useQuery<DuLieuTenMien>({ queryKey: khoa, queryFn: async () => (await platformApi.get(`/platform/orgs/${orgId}/domains`)).data })
  const [domain, setDomain] = useState('')
  const [slug, setSlug] = useState('')
  const [xacMinhLuon, setXacMinhLuon] = useState(false)
  const loi = (e: unknown) => toast.error(platformApiError(e))
  const dat = (d: DuLieuTenMien) => qc.setQueryData(khoa, d)

  const them = useMutation({ mutationFn: async (body: Record<string, unknown>) => (await platformApi.post(`/platform/orgs/${orgId}/domains`, body)).data as DuLieuTenMien, onSuccess: (d) => { dat(d); setDomain(''); setSlug(''); toast.success('Đã thêm tên miền') }, onError: loi })
  const kiem = useMutation({ mutationFn: async (id: string) => (await platformApi.post(`/platform/orgs/${orgId}/domains/${id}/verify`)).data as DuLieuTenMien & { kiemTra: { ok: boolean; chiTiet: string } }, onSuccess: (d) => { dat(d); (d.kiemTra.ok ? toast.success : toast.error)(d.kiemTra.chiTiet) }, onError: loi })
  const chinh = useMutation({ mutationFn: async (id: string) => (await platformApi.post(`/platform/orgs/${orgId}/domains/${id}/primary`)).data as DuLieuTenMien, onSuccess: dat, onError: loi })
  const xoa = useMutation({ mutationFn: async (id: string) => (await platformApi.delete(`/platform/orgs/${orgId}/domains/${id}`)).data as DuLieuTenMien, onSuccess: dat, onError: loi })

  const { data: th } = useQuery<ThuongHieu>({ queryKey: ['platform', 'org', orgId, 'branding'], queryFn: async () => (await platformApi.get(`/platform/orgs/${orgId}/branding`)).data })
  const [form, setForm] = useState({ brandName: '', tagline: '', primaryColor: '', headerColor: '', hidePlatformBrand: false })
  useEffect(() => { if (th) setForm({ brandName: th.brandName ?? '', tagline: th.tagline ?? '', primaryColor: th.primaryColor ?? '', headerColor: th.headerColor ?? '', hidePlatformBrand: th.hidePlatformBrand }) }, [th])
  const luuTh = useMutation({
    mutationFn: async () => (await platformApi.put(`/platform/orgs/${orgId}/branding`, { brandName: form.brandName.trim() || null, tagline: form.tagline.trim() || null, primaryColor: form.primaryColor.trim() || null, headerColor: form.headerColor.trim() || null, hidePlatformBrand: form.hidePlatformBrand })).data,
    onSuccess: (d) => { qc.setQueryData(['platform', 'org', orgId, 'branding'], d); toast.success('Đã lưu thương hiệu') },
    onError: loi,
  })

  if (!data) return <Loader2 className="mx-auto animate-spin" />
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card className="lg:col-span-2">
        <CardHeader><CardTitle>Tên miền</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            {data.nenTang && (
              <div className="flex items-end gap-2">
                <div className="flex-1"><Label htmlFor="p-slug" className="text-xs">Subdomain nền tảng</Label>
                  <div className="flex items-center gap-1"><Input id="p-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="nhathuocabc" className="h-9" /><span className="text-sm text-muted-foreground">.{data.nenTang}</span></div></div>
                <Button size="sm" disabled={slug.length < 3 || them.isPending} onClick={() => them.mutate({ slug })}>Tạo</Button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="flex-1"><Label htmlFor="p-domain" className="text-xs">Tên miền riêng</Label><Input id="p-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="chat.nhathuocabc.vn" className="h-9" /></div>
              <Button size="sm" disabled={domain.length < 4 || them.isPending} onClick={() => them.mutate({ domain, xacMinhLuon })}>Thêm</Button>
            </div>
          </div>
          <label htmlFor="p-xm" className="flex items-center gap-2 text-xs text-muted-foreground"><Checkbox id="p-xm" checked={xacMinhLuon} onCheckedChange={(v) => setXacMinhLuon(!!v)} /> Xác nhận luôn (đã kiểm DNS bên ngoài, bỏ qua bản ghi TXT)</label>
          <div className="divide-y rounded-md border">
            {data.domains.length === 0 && <p className="p-3 text-sm text-muted-foreground">Chưa có tên miền — tổ chức dùng tên miền chung.</p>}
            {data.domains.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-2 p-2.5 text-sm">
                <span className="font-medium">{d.domain}</span>
                <Badge variant={d.status === 'verified' ? 'success' : 'warning'}>{d.status === 'verified' ? 'Đã xác minh' : 'Chờ DNS'}</Badge>
                <Badge variant="secondary">SSL: {d.sslStatus}</Badge>
                {d.isPrimary && <Star className="h-3.5 w-3.5 text-warning" />}
                {d.status !== 'verified' && d.huongDan && <code className="text-[11px] text-muted-foreground">TXT {d.huongDan.txt.ten} = {d.huongDan.txt.giaTri}</code>}
                <div className="ml-auto flex gap-1">
                  {d.status !== 'verified' && <Button size="sm" variant="ghost" onClick={() => kiem.mutate(d.id)}><RefreshCw /></Button>}
                  {d.status === 'verified' && !d.isPrimary && <Button size="sm" variant="ghost" onClick={() => chinh.mutate(d.id)}>Đặt chính</Button>}
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => { if (confirm(`Xoá ${d.domain}?`)) xoa.mutate(d.id) }}><Trash2 /></Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Thương hiệu</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label htmlFor="p-bn" className="text-xs">Tên thương hiệu</Label><Input id="p-bn" value={form.brandName} onChange={(e) => setForm((f) => ({ ...f, brandName: e.target.value }))} className="h-9" /></div>
          <div><Label htmlFor="p-tl" className="text-xs">Khẩu hiệu</Label><Input id="p-tl" value={form.tagline} onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))} className="h-9" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label htmlFor="p-mau" className="text-xs">Màu chủ đạo</Label><ChonMau id="p-mau" value={form.primaryColor} onChange={(v) => setForm((f) => ({ ...f, primaryColor: v }))} /></div>
            <div><Label htmlFor="p-header" className="text-xs">Màu header menu</Label><ChonMau id="p-header" value={form.headerColor} onChange={(v) => setForm((f) => ({ ...f, headerColor: v }))} /></div>
          </div>
          <label htmlFor="p-an" className="flex items-center justify-between text-sm">Ẩn thương hiệu nền tảng <Switch id="p-an" checked={form.hidePlatformBrand} onCheckedChange={(v) => setForm((f) => ({ ...f, hidePlatformBrand: v }))} /></label>
          <Button size="sm" disabled={luuTh.isPending} onClick={() => luuTh.mutate()}>Lưu thương hiệu</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Mức dùng · {data.hanMuc.goi?.ten ?? 'Tuỳ chỉnh'}</CardTitle></CardHeader>
        <CardContent className="space-y-1.5 text-sm">
          {Object.entries(TEN_HM).map(([k, ten]) => (
            <div key={k} className="flex justify-between"><span>{ten}</span><span className="tabular-nums text-muted-foreground">{data.hanMuc.dangDung[k]} / {data.hanMuc.gioiHan[k] == null ? '∞' : String(data.hanMuc.gioiHan[k])}</span></div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
